import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import klydoApiClient, { PopulatedKlydo } from '../../../api/klydo';

const PAGE_SIZE = 10;

export type UseSelectableKlydosInfiniteQueryProps = {
  searchText?: string;
  collectionId?: string;
};

/**
 * Klydos available to add to a collection.
 *
 * `availableForCollection` is the api flag for exactly this set: the artist's own
 * klydos that are not in a collection already and are not publicly published.
 * It deliberately does not look at `type`, so DRAFTS ARE INCLUDED — a klydo needs
 * no approval to join a collection. Sending `inCollection: false` instead would
 * be wrong twice over: it lets already-public klydos through, and the api ignores
 * `availableForCollection` whenever `inCollection` is present.
 */
const useSelectableKlydosInfiniteQuery = ({
  searchText = '',
  collectionId,
}: UseSelectableKlydosInfiniteQueryProps) => {
  const queryClient = useQueryClient();

  const { data, fetchNextPage, isLoading, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<PopulatedKlydo[], Error>({
      queryKey: ['selectableKlydosList', searchText, collectionId],
      queryFn: async ({ pageParam }) => {
        const { skip = 0 } = pageParam as { skip?: number };
        const searchOptions = {
          limit: PAGE_SIZE,
          skip,
          sort: { createdAt: -1 },
          filters: { availableForCollection: true },
          collectionId,
        };
        const res = await klydoApiClient.searchCollectionCandidates(
          searchText,
          searchOptions,
        );

        res.forEach((klydo) => {
          queryClient.setQueryData(['klydo', klydo.id], klydo);
        });

        return res;
      },
      getNextPageParam: (lastPage, allPages) => {
        const currentLength = allPages.flat().length;
        return lastPage.length < PAGE_SIZE
          ? undefined
          : { skip: currentLength };
      },
      initialPageParam: { skip: 0 },
    });

  const flatData = data?.pages.flat();

  return {
    data: flatData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
};

export default useSelectableKlydosInfiniteQuery;
